import type { Element as HASTElement } from 'hast';
import { toHtml } from 'hast-util-to-html';

import {
  DEFAULT_THEMES,
  DIFFS_TAG_NAME,
  HEADER_METADATA_SLOT_ID,
  UNSAFE_CSS_ATTRIBUTE,
} from '../constants';
import {
  LineSelectionManager,
  type LineSelectionOptions,
  type SelectedLineRange,
  pluckLineSelectionOptions,
} from '../managers/LineSelectionManager';
import {
  type GetHoveredLineResult,
  MouseEventManager,
  type MouseEventManagerBaseOptions,
  pluckMouseEventOptions,
} from '../managers/MouseEventManager';
import { ResizeManager } from '../managers/ResizeManager';
import { ScrollSyncManager } from '../managers/ScrollSyncManager';
import { VirtualizationManager } from '../managers/VirtualizationManager';
import {
  DiffHunksRenderer,
  type HunksRenderResult,
} from '../renderers/DiffHunksRenderer';
import { SVGSpriteSheet } from '../sprite';
import type {
  BaseDiffOptions,
  DiffLineAnnotation,
  ExpansionDirections,
  FileContents,
  FileDiffMetadata,
  HunkData,
  HunkSeparators,
  RenderHeaderMetadataCallback,
  ThemeTypes,
} from '../types';
import { areFilesEqual } from '../utils/areFilesEqual';
import { createAnnotationWrapperNode } from '../utils/createAnnotationWrapperNode';
import { createCodeNode } from '../utils/createCodeNode';
import { createHoverContentNode } from '../utils/createHoverContentNode';
import { createUnsafeCSSStyleNode } from '../utils/createUnsafeCSSStyleNode';
import { wrapUnsafeCSS } from '../utils/cssWrappers';
import { getLineAnnotationName } from '../utils/getLineAnnotationName';
import { parseDiffFromFile } from '../utils/parseDiffFromFile';
import { prerenderHTMLIfNecessary } from '../utils/prerenderHTMLIfNecessary';
import { setPreNodeProperties } from '../utils/setWrapperNodeProps';
import type { WorkerPoolManager } from '../worker';
import { DiffsContainerLoaded } from './web-components';

export interface FileDiffRenderProps<LAnnotation> {
  fileDiff?: FileDiffMetadata;
  oldFile?: FileContents;
  newFile?: FileContents;
  forceRender?: boolean;
  fileContainer?: HTMLElement;
  containerWrapper?: HTMLElement;
  lineAnnotations?: DiffLineAnnotation<LAnnotation>[];
}

export interface FileDiffHydrationProps<LAnnotation>
  extends Omit<FileDiffRenderProps<LAnnotation>, 'fileContainer'> {
  fileContainer: HTMLElement;
  prerenderedHTML?: string;
}

export interface FileDiffOptions<LAnnotation>
  extends Omit<BaseDiffOptions, 'hunkSeparators'>,
    MouseEventManagerBaseOptions<'diff'>,
    LineSelectionOptions {
  hunkSeparators?:
    | Exclude<HunkSeparators, 'custom'>
    | ((
        hunk: HunkData,
        instance: FileDiff<LAnnotation>
      ) => HTMLElement | DocumentFragment);
  disableFileHeader?: boolean;
  renderHeaderMetadata?: RenderHeaderMetadataCallback;
  renderAnnotation?(
    annotation: DiffLineAnnotation<LAnnotation>
  ): HTMLElement | undefined;
  renderHoverUtility?(
    getHoveredRow: () => GetHoveredLineResult<'diff'> | undefined
  ): HTMLElement | null;
}

let instanceId = -1;

export class FileDiff<LAnnotation = undefined> {
  // NOTE(amadeus): We sorta need this to ensure the web-component file is
  // properly loaded
  static LoadedCustomComponent: boolean = DiffsContainerLoaded;

  readonly __id: number = ++instanceId;

  private fileContainer: HTMLElement | undefined;
  private spriteSVG: SVGElement | undefined;
  private pre: HTMLPreElement | undefined;
  private unsafeCSSStyle: HTMLStyleElement | undefined;
  private hoverContent: HTMLElement | undefined;

  private headerElement: HTMLElement | undefined;
  private headerMetadata: HTMLElement | undefined;
  private customHunkElements: HTMLElement[] = [];
  private errorWrapper: HTMLElement | undefined;

  private hunksRenderer: DiffHunksRenderer<LAnnotation>;
  private resizeManager: ResizeManager;
  private scrollSyncManager: ScrollSyncManager;
  private mouseEventManager: MouseEventManager<'diff'>;
  private lineSelectionManager: LineSelectionManager;
  private virtualizationManagerDeletions: VirtualizationManager;
  private virtualizationManagerAdditions: VirtualizationManager;

  private annotationElements: HTMLElement[] = [];
  private lineAnnotations: DiffLineAnnotation<LAnnotation>[] = [];

  private oldFile: FileContents | undefined;
  private newFile: FileContents | undefined;
  private fileDiff: FileDiffMetadata | undefined;

  constructor(
    public options: FileDiffOptions<LAnnotation> = { theme: DEFAULT_THEMES },
    private workerManager?: WorkerPoolManager | undefined,
    // NOTE(amadeus): Temp hack while we use this component in a react context
    private isContainerManaged = false
  ) {
    this.hunksRenderer = new DiffHunksRenderer(
      {
        ...options,
        hunkSeparators:
          typeof options.hunkSeparators === 'function'
            ? 'custom'
            : options.hunkSeparators,
      },
      this.handleHighlightRender,
      this.workerManager
    );
    this.resizeManager = new ResizeManager();
    this.scrollSyncManager = new ScrollSyncManager();
    this.mouseEventManager = new MouseEventManager(
      'diff',
      pluckMouseEventOptions(
        options,
        typeof options.hunkSeparators === 'function' ||
          (options.hunkSeparators ?? 'line-info') === 'line-info'
          ? this.handleExpandHunk
          : undefined
      )
    );
    this.lineSelectionManager = new LineSelectionManager(
      pluckLineSelectionOptions(options)
    );
    this.virtualizationManagerDeletions = new VirtualizationManager(
      options.virtualization
    );
    this.virtualizationManagerAdditions = new VirtualizationManager(
      options.virtualization
    );
    this.workerManager?.subscribeToThemeChanges(this);
  }

  private handleHighlightRender = (): void => {
    this.rerender();
  };

  // FIXME(amadeus): This is a bit of a looming issue that I'll need to resolve:
  // * Do we publicly allow merging of options or do we have individualized setters?
  // * When setting new options, we need to figure out what settings require a
  //   re-render and which can just be applied more elegantly
  // * There's also an issue of options that live here on the File class and
  //   those that live on the Hunk class, and it's a bit of an issue with passing
  //   settings down and mirroring them (not great...)
  setOptions(options: FileDiffOptions<LAnnotation> | undefined): void {
    if (options == null) return;
    this.options = options;
    this.hunksRenderer.setOptions({
      ...this.options,
      hunkSeparators:
        typeof options.hunkSeparators === 'function'
          ? 'custom'
          : options.hunkSeparators,
    });
    this.mouseEventManager.setOptions(
      pluckMouseEventOptions(
        options,
        typeof options.hunkSeparators === 'function' ||
          (options.hunkSeparators ?? 'line-info') === 'line-info'
          ? this.handleExpandHunk
          : undefined
      )
    );
    this.lineSelectionManager.setOptions(pluckLineSelectionOptions(options));
    if (options.virtualization) {
      this.virtualizationManagerDeletions.setOptions(options.virtualization);
      this.virtualizationManagerAdditions.setOptions(options.virtualization);
    }
  }

  private mergeOptions(options: Partial<FileDiffOptions<LAnnotation>>): void {
    this.options = { ...this.options, ...options };
  }

  setThemeType(themeType: ThemeTypes): void {
    if ((this.options.themeType ?? 'system') === themeType) {
      return;
    }
    this.mergeOptions({ themeType });
    this.hunksRenderer.setThemeType(themeType);

    if (this.headerElement != null) {
      if (themeType === 'system') {
        delete this.headerElement.dataset.themeType;
      } else {
        this.headerElement.dataset.themeType = themeType;
      }
    }

    // Update pre element theme mode
    if (this.pre != null) {
      switch (themeType) {
        case 'system':
          delete this.pre.dataset.themeType;
          break;
        case 'light':
        case 'dark':
          this.pre.dataset.themeType = themeType;
          break;
      }
    }
  }

  getHoveredLine = (): GetHoveredLineResult<'diff'> | undefined => {
    return this.mouseEventManager.getHoveredLine();
  };

  setLineAnnotations(lineAnnotations: DiffLineAnnotation<LAnnotation>[]): void {
    this.lineAnnotations = lineAnnotations;
  }

  setSelectedLines(range: SelectedLineRange | null): void {
    this.lineSelectionManager.setSelection(range);
  }

  cleanUp(): void {
    this.hunksRenderer.cleanUp();
    this.resizeManager.cleanUp();
    this.mouseEventManager.cleanUp();
    this.scrollSyncManager.cleanUp();
    this.lineSelectionManager.cleanUp();
    this.virtualizationManagerDeletions.cleanUp();
    this.virtualizationManagerAdditions.cleanUp();
    this.workerManager?.unsubscribeToThemeChanges(this);
    this.workerManager = undefined;

    // Clean up the data
    this.fileDiff = undefined;
    this.oldFile = undefined;
    this.newFile = undefined;

    // Clean up the elements
    if (!this.isContainerManaged) {
      this.fileContainer?.parentNode?.removeChild(this.fileContainer);
    }
    if (this.fileContainer?.shadowRoot != null) {
      this.fileContainer.shadowRoot.innerHTML = '';
    }
    this.fileContainer = undefined;
    this.pre = undefined;
    this.headerElement = undefined;
    this.errorWrapper = undefined;
  }

  hydrate(props: FileDiffHydrationProps<LAnnotation>): void {
    const { fileContainer, prerenderedHTML } = props;
    prerenderHTMLIfNecessary(fileContainer, prerenderedHTML);
    for (const element of Array.from(
      fileContainer.shadowRoot?.children ?? []
    )) {
      if (element instanceof SVGElement) {
        this.spriteSVG = element;
        continue;
      }
      if (!(element instanceof HTMLElement)) {
        continue;
      }
      if (element instanceof HTMLPreElement) {
        this.pre = element;
        continue;
      }
      if ('diffsHeader' in element.dataset) {
        this.headerElement = element;
        continue;
      }
      if (
        element instanceof HTMLStyleElement &&
        element.hasAttribute(UNSAFE_CSS_ATTRIBUTE)
      ) {
        this.unsafeCSSStyle = element;
        continue;
      }
    }
    // If we have no pre tag, then we should render
    if (this.pre == null) {
      this.render(props);
    }
    // Otherwise orchestrate our setup
    else {
      const { lineAnnotations, oldFile, newFile, fileDiff } = props;
      this.fileContainer = fileContainer;
      delete this.pre.dataset.dehydrated;

      this.lineAnnotations = lineAnnotations ?? this.lineAnnotations;
      this.newFile = newFile;
      this.oldFile = oldFile;
      this.fileDiff =
        fileDiff ??
        (oldFile != null && newFile != null
          ? parseDiffFromFile(oldFile, newFile)
          : undefined);

      this.hunksRenderer.hydrate(this.fileDiff);
      // FIXME(amadeus): not sure how to handle this yet...
      // this.renderSeparators();
      this.renderAnnotations();
      this.renderHoverUtility();
      this.injectUnsafeCSS();
      this.mouseEventManager.setup(this.pre);
      this.lineSelectionManager.setup(this.pre);
      if ((this.options.overflow ?? 'scroll') === 'scroll') {
        this.resizeManager.setup(this.pre);
        this.scrollSyncManager.setup(this.pre);
      }
    }
  }

  rerender(): void {
    if (this.fileDiff == null && this.newFile == null && this.oldFile == null) {
      return;
    }
    this.render({
      oldFile: this.oldFile,
      newFile: this.newFile,
      fileDiff: this.fileDiff,
      forceRender: true,
    });
  }

  handleExpandHunk = (
    hunkIndex: number,
    direction: ExpansionDirections
  ): void => {
    this.expandHunk(hunkIndex, direction);
  };

  expandHunk(hunkIndex: number, direction: ExpansionDirections): void {
    this.hunksRenderer.expandHunk(hunkIndex, direction);
    this.rerender();
  }

  render({
    oldFile,
    newFile,
    fileDiff,
    forceRender = false,
    lineAnnotations,
    fileContainer,
    containerWrapper,
  }: FileDiffRenderProps<LAnnotation>): void {
    const filesDidChange =
      oldFile != null &&
      newFile != null &&
      (!areFilesEqual(oldFile, this.oldFile) ||
        !areFilesEqual(newFile, this.newFile));
    const annotationsChanged =
      lineAnnotations != null &&
      (lineAnnotations.length > 0 || this.lineAnnotations.length > 0)
        ? lineAnnotations !== this.lineAnnotations
        : false;
    if (
      !forceRender &&
      !annotationsChanged &&
      // If using the fileDiff API, lets check to see if they are equal to
      // avoid doing work
      ((fileDiff != null && fileDiff === this.fileDiff) ||
        // If using the oldFile/newFile API then lets check to see if they are
        // equal
        (fileDiff == null && !filesDidChange))
    ) {
      return;
    }

    this.oldFile = oldFile;
    this.newFile = newFile;
    if (fileDiff != null) {
      this.fileDiff = fileDiff;
    } else if (oldFile != null && newFile != null && filesDidChange) {
      this.fileDiff = parseDiffFromFile(oldFile, newFile);
    }

    if (lineAnnotations != null) {
      this.setLineAnnotations(lineAnnotations);
    }
    if (this.fileDiff == null) {
      return;
    }
    this.hunksRenderer.setOptions({
      ...this.options,
      hunkSeparators:
        typeof this.options.hunkSeparators === 'function'
          ? 'custom'
          : this.options.hunkSeparators,
    });

    this.hunksRenderer.setLineAnnotations(this.lineAnnotations);

    const { disableFileHeader = false } = this.options;

    if (disableFileHeader) {
      // Remove existing header from DOM
      if (this.headerElement != null) {
        this.headerElement.parentNode?.removeChild(this.headerElement);
        this.headerElement = undefined;
      }
    }
    fileContainer = this.getOrCreateFileContainer(
      fileContainer,
      containerWrapper
    );

    try {
      const hunksResult = this.hunksRenderer.renderDiff(this.fileDiff);
      if (hunksResult == null) {
        if (this.workerManager != null && !this.workerManager.isInitialized()) {
          void this.workerManager.initialize().then(() => this.rerender());
        }
        return;
      }

      if (hunksResult.headerElement != null) {
        this.applyHeaderToDOM(hunksResult.headerElement, fileContainer);
      }
      const pre = this.getOrCreatePreNode(fileContainer);
      this.applyHunksToDOM(pre, hunksResult);
      this.renderSeparators(hunksResult.hunkData);
      this.renderAnnotations();
      this.renderHoverUtility();
    } catch (error: unknown) {
      if (error instanceof Error) {
        this.applyErrorToDOM(error, fileContainer);
      }
    }
  }

  private renderSeparators(hunkData: HunkData[]): void {
    const { hunkSeparators } = this.options;
    if (
      this.isContainerManaged ||
      this.fileContainer == null ||
      typeof hunkSeparators !== 'function'
    ) {
      return;
    }
    for (const element of this.customHunkElements) {
      element.parentNode?.removeChild(element);
    }
    this.customHunkElements.length = 0;
    for (const hunk of hunkData) {
      const element = document.createElement('div');
      element.style.display = 'contents';
      element.slot = hunk.slotName;
      element.appendChild(hunkSeparators(hunk, this));
      this.fileContainer.appendChild(element);
      this.customHunkElements.push(element);
    }
  }

  private renderAnnotations(): void {
    if (this.isContainerManaged || this.fileContainer == null) {
      return;
    }
    // Handle annotation elements
    for (const element of this.annotationElements) {
      element.parentNode?.removeChild(element);
    }
    this.annotationElements.length = 0;

    const { renderAnnotation } = this.options;
    if (renderAnnotation != null && this.lineAnnotations.length > 0) {
      for (const annotation of this.lineAnnotations) {
        const content = renderAnnotation(annotation);
        if (content == null) continue;
        const el = createAnnotationWrapperNode(
          getLineAnnotationName(annotation)
        );
        el.appendChild(content);
        this.annotationElements.push(el);
        this.fileContainer.appendChild(el);
      }
    }
  }

  private renderHoverUtility() {
    const { renderHoverUtility } = this.options;
    if (this.fileContainer == null || renderHoverUtility == null) return;
    if (this.hoverContent == null) {
      this.hoverContent = createHoverContentNode();
      this.fileContainer.appendChild(this.hoverContent);
    }
    const element = renderHoverUtility(this.mouseEventManager.getHoveredLine);
    this.hoverContent.innerHTML = '';
    if (element != null) {
      this.hoverContent.appendChild(element);
    }
  }

  private getOrCreateFileContainer(
    fileContainer?: HTMLElement,
    parentNode?: HTMLElement
  ): HTMLElement {
    this.fileContainer =
      fileContainer ??
      this.fileContainer ??
      document.createElement(DIFFS_TAG_NAME);
    if (parentNode != null && this.fileContainer.parentNode !== parentNode) {
      parentNode.appendChild(this.fileContainer);
    }
    if (this.spriteSVG == null) {
      const fragment = document.createElement('div');
      fragment.innerHTML = SVGSpriteSheet;
      const firstChild = fragment.firstChild;
      if (firstChild instanceof SVGElement) {
        this.spriteSVG = firstChild;
        this.fileContainer.shadowRoot?.appendChild(this.spriteSVG);
      }
    }
    return this.fileContainer;
  }

  getFileContainer(): HTMLElement | undefined {
    return this.fileContainer;
  }

  private getOrCreatePreNode(container: HTMLElement): HTMLPreElement {
    // If we haven't created a pre element yet, lets go ahead and do that
    if (this.pre == null) {
      this.pre = document.createElement('pre');
      container.shadowRoot?.appendChild(this.pre);
    }
    // If we have a new parent container for the pre element, lets go ahead and
    // move it into the new container
    else if (this.pre.parentNode !== container) {
      container.shadowRoot?.appendChild(this.pre);
    }
    return this.pre;
  }

  private applyHeaderToDOM(
    headerAST: HASTElement,
    container: HTMLElement
  ): void {
    this.cleanupErrorWrapper();
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = toHtml(headerAST);
    const newHeader = tempDiv.firstElementChild;
    if (!(newHeader instanceof HTMLElement)) {
      return;
    }
    if (this.headerElement != null) {
      container.shadowRoot?.replaceChild(newHeader, this.headerElement);
    } else {
      container.shadowRoot?.prepend(newHeader);
    }
    this.headerElement = newHeader;

    if (this.isContainerManaged) return;

    const { renderHeaderMetadata } = this.options;
    if (this.headerMetadata != null) {
      this.headerMetadata.parentNode?.removeChild(this.headerMetadata);
    }
    const content =
      renderHeaderMetadata?.({
        oldFile: this.oldFile,
        newFile: this.newFile,
        fileDiff: this.fileDiff,
      }) ?? undefined;
    if (content != null) {
      this.headerMetadata = document.createElement('div');
      this.headerMetadata.slot = HEADER_METADATA_SLOT_ID;
      if (content instanceof Element) {
        this.headerMetadata.appendChild(content);
      } else {
        this.headerMetadata.innerText = `${content}`;
      }
      container.appendChild(this.headerMetadata);
    }
  }

  private injectUnsafeCSS(): void {
    if (this.fileContainer?.shadowRoot == null) {
      return;
    }
    const { unsafeCSS } = this.options;

    if (unsafeCSS == null || unsafeCSS === '') {
      return;
    }

    // Create or update the style element
    if (this.unsafeCSSStyle == null) {
      this.unsafeCSSStyle = createUnsafeCSSStyleNode();
      this.fileContainer.shadowRoot.appendChild(this.unsafeCSSStyle);
    }
    // Wrap in @layer unsafe to match SSR behavior
    this.unsafeCSSStyle.innerText = wrapUnsafeCSS(unsafeCSS);
  }

  private applyHunksToDOM(
    pre: HTMLPreElement,
    result: HunksRenderResult
  ): void {
    this.cleanupErrorWrapper();
    this.applyPreNodeAttributes(pre, result);

    // Clear existing content
    pre.innerHTML = '';

    // Check if virtualization should be enabled
    const { virtualization } = this.options;
    const lineCount = result.totalLines;
    const shouldVirtualize =
      virtualization?.enabled && lineCount >= (virtualization.threshold ?? 500);

    let codeDeletions: HTMLElement | undefined;
    let codeAdditions: HTMLElement | undefined;
    // Create code elements and insert HTML content
    if (result.unifiedAST != null) {
      const codeUnified = createCodeNode({ columnType: 'unified' });

      if (shouldVirtualize) {
        // FAST PATH: Pass HAST AST nodes directly to virtualizer.
        // Skips toHtml() on full AST + splitHTMLIntoElements().
        // Only visible lines (~60) get toHtml() — lazily on scroll.
        pre.appendChild(codeUnified);
        this.virtualizationManagerDeletions.setOptions(virtualization);
        this.virtualizationManagerDeletions.setupFromHAST(
          codeUnified,
          result.unifiedAST
        );
      } else {
        const unifiedHtml = this.hunksRenderer.renderPartialHTML(
          result.unifiedAST
        );
        codeUnified.innerHTML = unifiedHtml;
        pre.appendChild(codeUnified);
        this.virtualizationManagerDeletions.cleanUp();
      }
      this.virtualizationManagerAdditions.cleanUp();
    } else {
      if (result.deletionsAST != null) {
        codeDeletions = createCodeNode({ columnType: 'deletions' });

        if (shouldVirtualize) {
          pre.appendChild(codeDeletions);
          this.virtualizationManagerDeletions.setOptions(virtualization);
          this.virtualizationManagerDeletions.setupFromHAST(
            codeDeletions,
            result.deletionsAST
          );
        } else {
          const deletionsHtml = this.hunksRenderer.renderPartialHTML(
            result.deletionsAST
          );
          codeDeletions.innerHTML = deletionsHtml;
          pre.appendChild(codeDeletions);
          this.virtualizationManagerDeletions.cleanUp();
        }
      }
      if (result.additionsAST != null) {
        codeAdditions = createCodeNode({ columnType: 'additions' });

        if (shouldVirtualize) {
          pre.appendChild(codeAdditions);
          this.virtualizationManagerAdditions.setOptions(virtualization);
          this.virtualizationManagerAdditions.setupFromHAST(
            codeAdditions,
            result.additionsAST
          );
        } else {
          const additionsHtml = this.hunksRenderer.renderPartialHTML(
            result.additionsAST
          );
          codeAdditions.innerHTML = additionsHtml;
          pre.appendChild(codeAdditions);
          this.virtualizationManagerAdditions.cleanUp();
        }
      }
    }

    this.injectUnsafeCSS();

    this.mouseEventManager.setup(pre);
    this.lineSelectionManager.setup(pre);
    if ((this.options.overflow ?? 'scroll') === 'scroll') {
      this.resizeManager.setup(pre);
      this.scrollSyncManager.setup(pre, codeDeletions, codeAdditions);
    } else {
      this.resizeManager.cleanUp();
      this.scrollSyncManager.cleanUp();
    }
  }

  private applyPreNodeAttributes(
    pre: HTMLPreElement,
    {
      themeStyles,
      baseThemeType,
      additionsAST,
      deletionsAST,
      totalLines,
    }: HunksRenderResult
  ): void {
    const {
      diffIndicators = 'bars',
      disableBackground = false,
      disableLineNumbers = false,
      overflow = 'scroll',
      themeType = 'system',
      diffStyle = 'split',
    } = this.options;
    const split =
      diffStyle === 'unified'
        ? false
        : additionsAST != null && deletionsAST != null;
    setPreNodeProperties({
      pre,
      diffIndicators,
      disableBackground,
      disableLineNumbers,
      overflow,
      split,
      themeStyles,
      themeType: baseThemeType ?? themeType,
      totalLines,
    });
  }

  private applyErrorToDOM(error: Error, container: HTMLElement) {
    this.cleanupErrorWrapper();
    const pre = this.getOrCreatePreNode(container);
    pre.innerHTML = '';
    pre.parentNode?.removeChild(pre);
    this.pre = undefined;
    const shadowRoot =
      container.shadowRoot ?? container.attachShadow({ mode: 'open' });
    this.errorWrapper ??= document.createElement('div');
    this.errorWrapper.dataset.errorWrapper = '';
    this.errorWrapper.innerHTML = '';
    shadowRoot.appendChild(this.errorWrapper);
    const errorMessage = document.createElement('div');
    errorMessage.dataset.errorMessage = '';
    errorMessage.innerText = error.message;
    this.errorWrapper.appendChild(errorMessage);
    const errorStack = document.createElement('pre');
    errorStack.dataset.errorStack = '';
    errorStack.innerText = error.stack ?? 'No Error Stack';
    this.errorWrapper.appendChild(errorStack);
  }

  private cleanupErrorWrapper() {
    this.errorWrapper?.parentNode?.removeChild(this.errorWrapper);
    this.errorWrapper = undefined;
  }
}
